import * as React from "react";
import { useState, useMemo, useEffect } from "react";
import { Button } from "@/common/components/ui/button";
// import { Input } from "@/common/components/ui/input";
import { Badge } from "@/projects/FinEaseHomeAndAdmin/components/ui/badge";
import CommonModal from "@/common/components/common/CommonModal";
// import { Tabs, TabsList, TabsTrigger } from "@/common/components/ui/tabs";
import {
  Download,
  Eye,
  CheckCircle,
  XCircle,
  FileText,
  User,
  RefreshCw,
  TrendingUp,
  Target,
} from "lucide-react";
import { TableColumnsType } from "antd";
import { AntDTable } from "@/common/components/ui";
// import {
//   Select,
//   SelectContent,
//   SelectItem,
//   SelectTrigger,
//   SelectValue,
// } from "@/common/components/ui/select";
// DropdownMenu is not available in common/components/ui, using inline buttons instead
/* import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"; */
import { notify } from "@/common/components/common/Notification";
import Loader from "@/common/components/ui/loader";
import IPOService from "@/services/IPO/APIs/IPOService";
import { GetApplicationGroup } from "@/utils/types/IPO/ipo";
import * as XLSX from "xlsx";

// const stats = [
//   {
//     label: "Total Applications",
//     value: "12,456",
//     change: "+18.5%",
//     positive: true,
//     icon: FileText,
//     color: "bg-primary/10 text-primary",
//   },
//   {
//     label: "Approved",
//     value: "8,567",
//     change: "+12.3%",
//     positive: true,
//     icon: CheckCircle,
//     color: "bg-icon-teal text-primary",
//   },
//   {
//     label: "Pending Review",
//     value: "2,345",
//     change: "-8.2%",
//     positive: true,
//     icon: Clock,
//     color: "bg-icon-orange text-secondary",
//   },
//   {
//     label: "Rejected",
//     value: "1,544",
//     change: "+2.1%",
//     positive: false,
//     icon: XCircle,
//     color: "bg-icon-red text-destructive",
//   },
//   {
//     label: "Total Amount",
//     value: "₹185 Cr",
//     change: "+24.8%",
//     positive: true,
//     icon: IndianRupee,
//     color: "bg-icon-blue text-primary",
//   },
//   {
//     label: "Allotment Rate",
//     value: "68.5%",
//     change: "+5.2%",
//     positive: true,
//     icon: Target,
//     color: "bg-icon-purple text-secondary",
//   },
// ];

const AdminIPOApplications = () => {
  const [searchTerm, _setSearchTerm] = useState("");
  const [_statusFilter, _setStatusFilter] = useState("all");
  const [categoryFilter] = useState("all");
  const [ipoFilter, _setIpoFilter] = useState("all");
  const [selectedApplication, setSelectedApplication] =
    useState<GetApplicationGroup | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [applications, setApplications] = useState<GetApplicationGroup[]>([]);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<GetApplicationGroup[]>([]);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const response = await IPOService.getApplicationGroup();
      setApplications(response.data || []);
      return true;
    } catch (error) {
      console.error("Failed to fetch applications:", error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  // Column Filtering State
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(
    {}
  );

  const onSelectChange = (
    newSelectedRowKeys: React.Key[],
    newSelectedRows: GetApplicationGroup[]
  ) => {
    setSelectedRowKeys(newSelectedRowKeys);
    setSelectedRows(newSelectedRows);
  };

  const handleColumnFilterChange = (columnKey: string, value: string) => {
    setColumnFilters(prev => ({ ...prev, [columnKey]: value }));
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const filteredApplications = applications.filter(app => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      (app.clientName?.toLowerCase().includes(searchLower) ?? false) ||
      (app.ipoName?.toLowerCase().includes(searchLower) ?? false) ||
      (app.id?.toLowerCase().includes(searchLower) ?? false) ||
      (app.pan?.toLowerCase().includes(searchLower) ?? false) ||
      (app.applicationNumber?.toLowerCase().includes(searchLower) ?? false) ||
      (app.bidReferenceNumber?.toLowerCase().includes(searchLower) ?? false);

    // There isn't a status field in the interface. Assuming 'all' works for everything for now.
    const matchesStatus = true;
    const matchesCategory =
      categoryFilter === "all" || app.category === categoryFilter;
    const matchesIPO = ipoFilter === "all" || app.ipoName === ipoFilter;

    // Apply column-specific filters from AntDTable
    const matchesColumnFilters = Object.entries(columnFilters).every(
      ([key, value]) => {
        if (!value) {
          return true;
        }

        const cellValue = app[key as keyof typeof app];
        return cellValue
          ? String(cellValue).toLowerCase().includes(value.toLowerCase())
          : true;
      }
    );

    return (
      matchesSearch &&
      matchesStatus &&
      matchesCategory &&
      matchesIPO &&
      matchesColumnFilters
    );
  });

  const handleApprove = (app: GetApplicationGroup) => {
    notify.success(
      "Application Approved",
      `${app.clientName}'s application for ${app.ipoName} has been approved.`
    );
  };

  const handleReject = (app: GetApplicationGroup) => {
    notify.error(
      "Application Rejected",
      `${app.clientName}'s application has been rejected.`
    );
  };

  // const handleResendNotification = (app: GetApplicationGroup) => {
  //   notify.success(
  //     "Notification Sent",
  //     `Status notification sent to ${app.clientName}.`
  //   );
  // };

  const getUPIPaymentStatus = (status: string | number | null | undefined) => {
    if (
      status === null ||
      status === undefined ||
      status === "null" ||
      status === ""
    ) {
      return <div className="text-center text-muted-foreground">-</div>;
    }

    const statuses: Record<string, string> = {
      "0": "Request Sent",
      "1": "Request Failed",
      "10": "Request Accepted By Sponsor Bank",
      "11": "Rejected due to invalid UPI",
      "12": "Rejected by Sponsor Bank",
      "13": "Unsupported Investor Bank",
      "21": "Rejected by Investor Bank",
      "22": "Block/Release Request rejected by Client Bank due to Technical Reason",
      "31": "Rejected by Investor",
      "100": "Accepted by Investor",
      "110": "Block Released (Due to cancellation of order)",
    };

    const statusStr = String(status);
    const label = statuses[statusStr] || statusStr || "-";

    let variant: "default" | "destructive" | "secondary" | "outline" =
      "outline";
    if (["10", "100", "110"].includes(statusStr)) {
      variant = "default"; // Success/Accepted states
    } else if (["1", "11", "12", "13", "21", "22", "31"].includes(statusStr)) {
      variant = "destructive"; // Error/Rejected states
    } else if (statusStr === "0") {
      variant = "secondary"; // Pending state
    }

    return (
      <Badge
        variant={variant}
        className="mx-auto block max-w-full whitespace-normal text-center leading-tight"
      >
        {label}
      </Badge>
    );
  };

  const handleSyncApplications = async () => {
    const ok = await fetchApplications();
    if (ok) {
      notify.success("Synced", "IPO application data synced successfully.");
    } else {
      notify.error("Sync Failed", "Failed to sync IPO application data.");
    }
  };

  const handleExportReport = () => {
    if (selectedRows.length === 0) {
      notify.error("No Data", "Please select applications to export.");
      return;
    }

    const rows = selectedRows.map(app => ({
      "Application ID": app.applicationNumber ?? "",
      "Client Name": app.clientName ?? "",
      "IPO Bid Name": app.ipoName ?? "",
      "Member Code": app.groupCode ?? "",
      "Mobile Number": app.mobile ?? "",
      "PAN Card": app.pan ?? "",
      Quantity: app.quantity ?? "",
      Amount: app.amount ?? "",
      Depository: app.depository ?? "",
      Category: app.category ?? "",
      "Demat Account No": `${app.dpId ?? ""}${app.clientBenId ?? ""}`,
      "UPI ID": app.upi ?? "",
      Exchange: app.exchange ?? "",
      "UPI Status":
        app?.upiVerified === true ||
        String(app?.upiVerified).trim().toLowerCase() === "true"
          ? "Verified"
          : "Unverified",
      "Created On": app.created_on
        ? new Date(app.created_on).toLocaleDateString("en-IN", {
            day: "numeric",
            month: "short",
            year: "numeric",
          })
        : "",
      "Activity Type": app.activityType ?? "",
      "Reference Number": app.referenceNumber ?? "",
      "Allocation Status": app.allocationStatus ?? "",
      "Bid Ref Number": app.bidReferenceNumber ?? "",
      "DP Status": app.dpStatus ?? "",
      "UPI Payment Status": app.upiPaymentStatusFlag ?? "",
      "Exchange Status": app.exchangeStatus ?? "",
      Status: app.status ?? "",
      Reason: app.reason ?? "",
      "Alloted Qty": app.allotedQuantity ?? "",
      "IP Address": app.ipAddress ?? "",
      "Monarch Error": app.monarchErrorMessage ?? "",
      Timestamp: app.timestamp
        ? new Date(app.timestamp).toLocaleString("en-IN")
        : "",
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, "IPO Applications");

    const datePart = new Date().toISOString().split("T")[0];
    XLSX.writeFile(wb, `ipo_applications_report_${datePart}.xlsx`);
    notify.success("Exported", `${rows.length} application(s) exported.`);
  };

  const totalCount = filteredApplications.length;

  const columns: TableColumnsType<any> = useMemo(
    () => [
      {
        title: "Application ID",
        dataIndex: "applicationNumber",
        key: "applicationNumber",
        width: 150,
        fixed: "left",
        render: applicationNumber => (
          <span className="ml-4 font-mono text-sm font-medium text-primary">
            {applicationNumber ?? "—"}
          </span>
        ),
        sorter: (a, b) =>
          (a.applicationNumber ?? "").localeCompare(b.applicationNumber ?? ""),
      },
      {
        title: "Client Name",
        dataIndex: "clientName",
        key: "clientName",
        width: 300,
        fixed: "left",
        render: name => <span className="text-secondary">{name ?? "—"}</span>,
        sorter: (a, b) =>
          (a.clientName ?? "").localeCompare(b.clientName ?? ""),
      },
      {
        title: "IPO Bid Name",
        dataIndex: "ipoName",
        key: "ipoName",
        width: 320,
        render: ipoName => (
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-primary" />
            <span className="font-medium">{ipoName}</span>
          </div>
        ),
        sorter: (a, b) => (a.ipoName ?? "").localeCompare(b.ipoName ?? ""),
      },
      {
        title: "Member Code",
        dataIndex: "groupCode",
        key: "groupCode",
        width: 140,
        render: code => (
          <span className="font-medium text-secondary">{code ?? "—"}</span>
        ),
        sorter: (a, b) => (a.groupCode ?? "").localeCompare(b.groupCode ?? ""),
      },
      {
        title: "Mobile Number",
        dataIndex: "mobile",
        key: "mobile",
        width: 150,
        render: mobile => (
          <span className="text-muted-foreground">{mobile ?? "—"}</span>
        ),
      },
      {
        title: "Pan Card",
        dataIndex: "pan",
        key: "pan",
        width: 150,
        render: pan => (
          <span className="font-sans text-sm uppercase">{pan}</span>
        ),
      },
      {
        title: "Quantity",
        dataIndex: "quantity",
        key: "quantity",
        width: 120,
        align: "center",
        showFilter: false,
        sorter: (a, b) => (a.quantity ?? 0) - (b.quantity ?? 0),
      },
      {
        title: "Amount",
        dataIndex: "amount",
        key: "amount",
        width: 160,
        showFilter: false,
        align: "left",
        render: amount => (
          <span className="font-bold text-secondary">
            ₹{(amount ?? 0).toLocaleString()}
          </span>
        ),
        sorter: (a, b) => (a.amount ?? 0) - (b.amount ?? 0),
      },
      {
        title: "Depository",
        dataIndex: "depository",
        showFilter: false,
        key: "depository",
        width: 130,
        render: d => d ?? "—",
      },
      {
        title: "Category",
        dataIndex: "category",
        key: "category",
        width: 150,
        showFilter: false,
        render: category => (
          <Badge
            variant="outline"
            className={
              category === "HNI"
                ? "border-purple-300 text-purple-700"
                : category === "QIB"
                  ? "border-blue-300 text-blue-700"
                  : ""
            }
          >
            {category ?? "—"}
          </Badge>
        ),
      },
      {
        title: "Demat Account No",
        dataIndex: "dpId",
        key: "dpId",
        width: 160,
        render: (_, record) => (
          <span className="font-mono text-sm">
            {record.dpId + record.clientBenId
              ? record.dpId + record.clientBenId
              : "—"}
          </span>
        ),
      },
      // {
      //   title: "Client Ben ID",
      //   dataIndex: "clientBenId",
      //   key: "clientBenId",
      //   width: 160,
      //   render: id => <span className="font-mono text-xs">{id ?? "—"}</span>,
      // },
      {
        title: "UPI Id",
        dataIndex: "upi",
        key: "upi",
        width: 200,
        render: upi => <span className="text-primary/80">{upi ?? "—"}</span>,
      },
      {
        title: "Exchange",
        dataIndex: "exchange",
        showFilter: false,
        key: "exchange",
        width: 140,
        render: code => <Badge variant="secondary">{code ?? "—"}</Badge>,
      },
      {
        title: "UPI Status",
        dataIndex: "upiVerified",
        showFilter: false,
        key: "upiVerified",
        width: 140,
        render: (_, record: any) => {
          const isVerified =
            record?.upiVerified === true ||
            String(record?.upiVerified).trim().toLowerCase() === "true";

          return (
            <Badge variant={isVerified ? "default" : "destructive"}>
              {isVerified ? "Verified" : "Unverified"}
            </Badge>
          );
        },
      },
      {
        title: "Created On",
        dataIndex: "created_on",
        showFilter: false,
        key: "created_on",
        width: 160,
        render: date => {
          if (!date) {
            return "—";
          }
          return new Date(date).toLocaleDateString("en-IN", {
            day: "numeric",
            month: "short",
            year: "numeric",
          });
        },
        sorter: (a, b) =>
          (a.created_on ?? "").localeCompare(b.created_on ?? ""),
      },
      {
        title: "Activity Type",
        dataIndex: "activityType",
        key: "activityType",
        width: 130,
        render: (val: string) => val ?? "—",
      },
      {
        title: "Reference Number",
        dataIndex: "referenceNumber",
        key: "referenceNumber",
        width: 180,
        render: (val: string) => <Badge variant="outline">{val ?? "—"}</Badge>,
      },
      {
        title: "Allocation Status",
        dataIndex: "allocationStatus",
        key: "allocationStatus",
        width: 180,
        render: (val: string) => <Badge variant="outline">{val ?? "—"}</Badge>,
      },
      {
        title: "Bid Ref Number",
        dataIndex: "bidReferenceNumber",
        key: "bidReferenceNumber",
        width: 160,
        align: "center",
        render: (val: string) => (
          <span className="font-sans text-sm">{val ?? "-"}</span>
        ),
      },
      {
        title: "DP Status",
        dataIndex: "dpStatus",
        key: "dpStatus",
        width: 130,
        align: "center",
        render: (val: string) => val ?? "-",
      },
      {
        title: "UPI Payment Status",
        dataIndex: "upiPaymentStatusFlag",
        key: "upiPaymentStatusFlag",
        width: 200,
        align: "center",
        render: (val: string | number) => getUPIPaymentStatus(val),
      },
      {
        title: "Exchange Status",
        dataIndex: "exchangeStatus",
        key: "exchangeStatus",
        width: 180,
        align: "center",
        render: (val: string) => val ?? "-",
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 130,
        align: "center",
        render: (val: string) =>
          val ? <Badge variant="secondary">{val}</Badge> : "-",
      },
      {
        title: "Reason",
        dataIndex: "reason",
        key: "reason",
        showFilter: false,
        width: 180,
        render: (val: string) => (
          <span className="text-xs text-muted-foreground">{val ?? "—"}</span>
        ),
      },
      {
        title: "Alloted Qty",
        dataIndex: "allotedQuantity",
        showFilter: false,
        key: "allotedQuantity",
        width: 120,
        render: (val: string) => val ?? "—",
      },
      {
        title: "IP Address",
        dataIndex: "ipAddress",
        showFilter: false,
        key: "ipAddress",
        width: 130,
        render: (val: string) => (
          <span className="font-mono text-xs">{val ?? "—"}</span>
        ),
      },
      {
        title: "Monarch Error",
        dataIndex: "monarchErrorMessage",
        showFilter: false,
        key: "monarchErrorMessage",
        width: 180,
        render: (val: string) => (
          <span className="text-xs text-red-500">{val ?? "—"}</span>
        ),
      },
      {
        title: "Timestamp",
        dataIndex: "timestamp",
        showFilter: false,
        key: "timestamp",
        width: 160,
        render: (val: string) =>
          val
            ? new Date(val).toLocaleDateString("en-IN", {
                day: "numeric",
                month: "short",
                year: "numeric",
              })
            : "—",
      },
      {
        title: "Actions",
        key: "actions",
        showFilter: false,
        width: 120,
        align: "center",
        fixed: "right",
        render: (_, app) => (
          <div className="flex justify-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => {
                setSelectedApplication(app);
                setIsViewModalOpen(true);
              }}
            >
              <Eye className="h-4 w-4" />
            </Button>
          </div>
        ),
      },
    ],
    [filteredApplications]
  );

  return (
    <div className="min-h-screen space-y-6 bg-gray-50 p-6">
      {loading && <Loader message="Loading Applications..." />}
      {/* Enhanced Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-secondary via-secondary/95 to-primary/80 p-6 md:p-8">
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent" />
        <svg
          className="absolute right-0 top-0 h-full w-1/3 text-white/5"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <polygon fill="currentColor" points="50,0 100,0 100,100 0,100" />
        </svg>
        <div className="relative flex flex-col justify-between gap-4 md:flex-row md:items-center">
          <div className="flex items-center gap-4">
            <div className="rounded-xl bg-white/10 p-3 backdrop-blur-sm">
              <FileText className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold text-white">
                IPO Applications
              </h1>
              <p className="text-white/70">
                Review, approve, and manage all IPO applications
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              className="gap-2 border-white/20 bg-white/10 text-white hover:bg-white/20"
              onClick={handleSyncApplications}
              disabled={loading}
            >
              <RefreshCw
                className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
              />{" "}
              Sync
            </Button>
            <Button
              variant="outline"
              className="gap-2 border-white/20 bg-white/10 text-white hover:bg-white/20"
              onClick={handleExportReport}
              disabled={selectedRows.length === 0}
            >
              <Download className="h-4 w-4" />
              Export Report
              {selectedRows.length > 0 && (
                <span className="ml-1 rounded-full bg-white/25 px-1.5 py-0.5 text-[11px] font-semibold leading-none">
                  {selectedRows.length}
                </span>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      {/* <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
        {stats.map(stat => (
          <Card
            key={stat.label}
            className="finease-card transition-all hover:shadow-md"
          >
            <CardContent className="p-4">
              <div className="mb-2 flex items-start justify-between">
                <div className={`rounded-xl p-2.5 ${stat.color}`}>
                  <stat.icon className="h-5 w-5" />
                </div>
                <span
                  className={`flex items-center gap-0.5 text-xs font-medium ${stat.positive ? "text-primary" : "text-destructive"}`}
                >
                  {stat.positive ? (
                    <ArrowUpRight className="h-3 w-3" />
                  ) : (
                    <ArrowDownRight className="h-3 w-3" />
                  )}
                  {stat.change}
                </span>
              </div>
              <p className="text-2xl font-bold text-secondary">{stat.value}</p>
              <p className="mt-1 text-xs text-muted-foreground">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </div> */}

      {/* Filters */}
      {/* <Card className="rounded-2xl border bg-card shadow-sm">
        <CardContent className="p-6">
          <div className="flex flex-col gap-4 md:flex-row">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by User, IPO Name, ID, or PAN..."
                className="w-72 pl-10 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
            <Select value={ipoFilter} onValueChange={setIpoFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="IPO Name" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All IPOs</SelectItem>
                {uniqueIPOs.map(ipo => (
                  <SelectItem key={ipo} value={ipo}>
                    {ipo}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Tabs value={statusFilter} onValueChange={setStatusFilter}>
              <TabsList className="font-sans">
                <TabsTrigger className="font-sans" value="all">
                  All
                </TabsTrigger>
                <TabsTrigger className="font-sans" value="Open">
                  Open
                </TabsTrigger>
                <TabsTrigger className="font-sans" value="Upcoming">
                  Upcoming
                </TabsTrigger>
                <TabsTrigger className="font-sans" value="Closed">
                  Closed
                </TabsTrigger>
                <TabsTrigger className="font-sans" value="Listed">
                  Listed
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardContent>
      </Card> */}

      {/* Results Count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Showing{" "}
          <span className="font-semibold text-secondary">
            {filteredApplications.length}
          </span>{" "}
          applications
        </p>
        {/* <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="gap-1"
            disabled={selectedRowKeys.length === 0}
          >
            <CheckCircle className="h-4 w-4" /> Bulk Approve{" "}
            {selectedRowKeys.length > 0 && `(${selectedRowKeys.length})`}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="gap-1 text-red-600 hover:text-red-700"
            disabled={selectedRowKeys.length === 0}
          >
            <XCircle className="h-4 w-4" /> Bulk Reject{" "}
            {selectedRowKeys.length > 0 && `(${selectedRowKeys.length})`}
          </Button>
        </div> */}
      </div>

      {/* Applications Table */}
      <AntDTable
        data={filteredApplications}
        columns={columns}
        rowKey="id"
        loading={loading}
        rowSelection={rowSelection}
        showColumnFilter
        onFilterChange={handleColumnFilterChange}
        sticky
        primaryColor="#66A0CD"
        headerBg="#F9FAFB"
        showSettings
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: totalCount,
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          },
          showSizeChanger: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} IPOs`,
          pageSizeOptions: ["10", "20", "50", "100"],
        }}
        scroll={{ x: 3800 }}
        emptyText="No IPOs found"
        className="ant-table-custom"
      />

      {/* View Application Modal */}
      <CommonModal
        open={isViewModalOpen}
        onCancel={() => setIsViewModalOpen(false)}
        title={
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            <span className="font-bold text-secondary">
              Application Details - {selectedApplication?.applicationNumber}
            </span>
          </div>
        }
        footer={null}
        width={750}
      >
        {selectedApplication && (
          <div className="max-h-[70vh] space-y-6 overflow-y-auto px-2 py-4 font-sans">
            {/* Application & Identity */}
            <div>
              <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-secondary">
                <FileText className="h-4 w-4 text-primary" /> Application &
                Identity
              </h4>
              <div className="grid grid-cols-3 gap-4 rounded-xl border border-slate-100 bg-slate-50 p-4">
                <div>
                  <p className="mb-1 text-[11px] uppercase tracking-wider text-muted-foreground">
                    Application ID
                  </p>
                  <p className="font-mono text-sm font-semibold text-primary">
                    {selectedApplication.applicationNumber}
                  </p>
                </div>
                <div>
                  <p className="mb-1 text-[11px] uppercase tracking-wider text-muted-foreground">
                    Member Code
                  </p>
                  <p className="font-medium text-secondary">
                    {selectedApplication.groupCode ??
                      "FIN" + selectedApplication.applicationNumber.slice(-3)}
                  </p>
                </div>
                <div>
                  <p className="mb-1 text-[11px] uppercase tracking-wider text-muted-foreground">
                    DP ID
                  </p>
                  <p className="font-mono text-sm">
                    {selectedApplication.dpId ?? "IN300010"}
                  </p>
                </div>
              </div>
            </div>

            {/* Personal Details */}
            <div>
              <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-secondary">
                <User className="h-4 w-4 text-primary" /> Personal Information
              </h4>
              <div className="grid grid-cols-2 gap-x-8 gap-y-4 rounded-xl border border-slate-100 bg-slate-50 p-4">
                <div>
                  <p className="mb-1 text-[11px] uppercase tracking-wider text-muted-foreground">
                    Client Name
                  </p>
                  <p className="font-semibold text-secondary">
                    {selectedApplication.clientName}
                  </p>
                </div>
                <div>
                  <p className="mb-1 text-[11px] uppercase tracking-wider text-muted-foreground">
                    Mobile Number
                  </p>
                  <p className="font-medium">
                    {selectedApplication.mobile ?? "+91 98765-43210"}
                  </p>
                </div>
                <div>
                  <p className="mb-1 text-[11px] uppercase tracking-wider text-muted-foreground">
                    Pan Card
                  </p>
                  <p className="inline-block rounded border border-slate-200 bg-white px-2 py-0.5 font-mono font-medium uppercase">
                    {selectedApplication.pan}
                  </p>
                </div>
                <div>
                  <p className="mb-1 text-[11px] uppercase tracking-wider text-muted-foreground">
                    UPI ID
                  </p>
                  <p className="font-medium text-primary/80">
                    {selectedApplication.upi}
                  </p>
                </div>
              </div>
            </div>

            {/* Order & Financial Details */}
            <div>
              <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-secondary">
                <Target className="h-4 w-4 text-primary" /> Order & Status
              </h4>
              <div className="grid grid-cols-3 gap-4 rounded-xl border border-slate-100 bg-slate-50 p-4">
                <div>
                  <p className="mb-1 text-[11px] uppercase tracking-wider text-muted-foreground">
                    Quantity
                  </p>
                  <p className="font-semibold text-secondary">
                    {selectedApplication.quantity} Lots
                  </p>
                </div>
                <div>
                  <p className="mb-1 text-[11px] uppercase tracking-wider text-muted-foreground">
                    Amount
                  </p>
                  <p className="text-lg font-bold text-primary">
                    ₹{selectedApplication.amount.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="mb-1 text-[11px] uppercase tracking-wider text-muted-foreground">
                    Category
                  </p>
                  <Badge variant="outline" className="font-medium">
                    {selectedApplication.category}
                  </Badge>
                </div>
                <div className="col-span-1">
                  <p className="mb-1 text-[11px] uppercase tracking-wider text-muted-foreground">
                    UPI Status
                  </p>
                  <Badge
                    variant={
                      selectedApplication.upiVerified
                        ? "default"
                        : "destructive"
                    }
                  >
                    {selectedApplication.upiVerified ? "Verified" : "Pending"}
                  </Badge>
                </div>
                <div className="col-span-2">
                  <p className="mb-1 text-[11px] uppercase tracking-wider text-muted-foreground">
                    Exchange
                  </p>
                  <Badge
                    variant="secondary"
                    className="border-blue-100 bg-blue-50 font-medium text-blue-700"
                  >
                    {selectedApplication.exchange ?? "NSE"}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Additional Details */}
            <div>
              <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-secondary">
                <FileText className="h-4 w-4 text-primary" /> Additional Details
              </h4>
              <div className="grid grid-cols-3 gap-4 rounded-xl border border-slate-100 bg-slate-50 p-4">
                <div>
                  <p className="mb-1 font-sans text-[11px] uppercase tracking-wider text-muted-foreground">
                    Application No.
                  </p>
                  <p className="font-mono text-sm">
                    {selectedApplication.applicationNumber ?? "—"}
                  </p>
                </div>
                <div>
                  <p className="mb-1 font-sans text-[11px] uppercase tracking-wider text-muted-foreground">
                    Bid Reference No.
                  </p>
                  <p className="font-mono text-sm">
                    {selectedApplication.bidReferenceNumber ?? "—"}
                  </p>
                </div>
                <div>
                  <p className="mb-1 font-sans text-[11px] uppercase tracking-wider text-muted-foreground">
                    Reference Number
                  </p>
                  <p className="font-medium text-secondary">
                    {selectedApplication.referenceNumber ?? "—"}
                  </p>
                </div>
                <div>
                  <p className="mb-1 font-sans text-[11px] uppercase tracking-wider text-muted-foreground">
                    Allocation Status
                  </p>
                  <p className="font-medium text-secondary">
                    {selectedApplication.allocationStatus ?? "—"}
                  </p>
                </div>
                <div>
                  <p className="mb-1 font-sans text-[11px] uppercase tracking-wider text-muted-foreground">
                    Alloted Quantity
                  </p>
                  <p className="font-medium text-secondary">
                    {selectedApplication.allotedQuantity ?? "—"}
                  </p>
                </div>
                <div>
                  <p className="mb-1 font-sans text-[11px] uppercase tracking-wider text-muted-foreground">
                    Activity Type
                  </p>
                  <p className="font-medium text-secondary">
                    {selectedApplication.activityType ?? "—"}
                  </p>
                </div>
                <div>
                  <p className="mb-1 font-sans text-[11px] uppercase tracking-wider text-muted-foreground">
                    Status
                  </p>
                  <p className="font-medium text-secondary">
                    {selectedApplication.status ?? "—"}
                  </p>
                </div>
                <div>
                  <p className="mb-1 font-sans text-[11px] uppercase tracking-wider text-muted-foreground">
                    Exchange Status
                  </p>
                  <p className="font-medium text-secondary">
                    {selectedApplication.exchangeStatus ?? "—"}
                  </p>
                </div>
                <div>
                  <p className="mb-1 font-sans text-[11px] uppercase tracking-wider text-muted-foreground">
                    UPI Payment Status
                  </p>
                  <div>
                    {selectedApplication.upiPaymentStatusFlag
                      ? getUPIPaymentStatus(
                          selectedApplication.upiPaymentStatusFlag
                        )
                      : "—"}
                  </div>
                </div>
                <div>
                  <p className="mb-1 font-sans text-[11px] uppercase tracking-wider text-muted-foreground">
                    DP Verify Status
                  </p>
                  <p className="font-medium text-secondary">
                    {selectedApplication.dpVerStatusFlag ?? "—"}
                  </p>
                </div>
                <div>
                  <p className="mb-1 font-sans text-[11px] uppercase tracking-wider text-muted-foreground">
                    DP Status
                  </p>
                  <p className="font-medium text-secondary">
                    {selectedApplication.dpStatus ?? "—"}
                  </p>
                </div>
                <div>
                  <p className="mb-1 font-sans text-[11px] uppercase tracking-wider text-muted-foreground">
                    Reason
                  </p>
                  <p className="font-sans text-xs text-muted-foreground">
                    {selectedApplication.reason ?? "—"}
                  </p>
                </div>
                <div>
                  <p className="mb-1 font-sans text-[11px] uppercase tracking-wider text-muted-foreground">
                    Monarch Error
                  </p>
                  <p className="font-sans text-xs text-red-500">
                    {selectedApplication.monarchErrorMessage ?? "—"}
                  </p>
                </div>
                <div>
                  <p className="mb-1 font-sans text-[11px] uppercase tracking-wider text-muted-foreground">
                    IP Address
                  </p>
                  <p className="font-mono text-sm">
                    {selectedApplication.ipAddress ?? "—"}
                  </p>
                </div>
                <div>
                  <p className="mb-1 font-sans text-[11px] uppercase tracking-wider text-muted-foreground">
                    Timestamp
                  </p>
                  <p className="font-mono text-sm">
                    {selectedApplication.timestamp
                      ? new Date(selectedApplication.timestamp).toLocaleString(
                          "en-IN"
                        )
                      : "—"}
                  </p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              {!selectedApplication.upiVerified && (
                <>
                  <Button
                    className="gap-2"
                    onClick={() => {
                      handleApprove(selectedApplication);
                      setIsViewModalOpen(false);
                    }}
                  >
                    <CheckCircle className="h-4 w-4" /> Approve Application
                  </Button>
                  <Button
                    variant="destructive"
                    className="gap-2"
                    onClick={() => {
                      handleReject(selectedApplication);
                      setIsViewModalOpen(false);
                    }}
                  >
                    <XCircle className="h-4 w-4" /> Reject Application
                  </Button>
                </>
              )}
              {/* <Button
                variant="outline"
                className="gap-2"
                onClick={() => handleResendNotification(selectedApplication)}
              >
                <Send className="h-4 w-4" /> Send Notification
              </Button> */}
            </div>
          </div>
        )}
      </CommonModal>
    </div>
  );
};

export default AdminIPOApplications;
